import React, { useState } from "react";
import { getIdTokenResult, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import TallargeLogoFull from "../assets/Tallarge_final.png";
import {
  INTERNAL_ROUTES,
  isAllowedInternalPath,
} from "../internalRoutes";

const SYSTEM_ADMIN_ROLE = "systemAdmin";
const ORG_ADMIN_ROLE = "orgAdmin";
const OWNER_ROLE = "owner";
const DEPUTY_ROLE = "deputy";
const DRIVER_ROLE = "driver";
const DRIVER_BLOCK_REDIRECT_URL = "https://tallarge.com";

function resolvePortalFromClaims(user, claims) {
  if (claims?.systemAdmin === true || claims?.role === SYSTEM_ADMIN_ROLE) {
    return "systemAdmin";
  }

  if (
    user?.uid &&
    claims?.role === ORG_ADMIN_ROLE &&
    claims?.orgOwnerUid === user.uid
  ) {
    return "internal";
  }

  if (
    (claims?.role === OWNER_ROLE || claims?.role === DEPUTY_ROLE) &&
    typeof claims?.orgId === "string" &&
    claims.orgId.trim()
  ) {
    return "internal";
  }

  if (user?.uid && claims?.role === DRIVER_ROLE) {
    return "driver";
  }

  return "";
}

function normalizeRole(value) {
  return String(value ?? "").trim().toLowerCase();
}

function isDriverAccount(claims, profile) {
  const claimRole = normalizeRole(claims?.role);
  const profileRole = normalizeRole(
    profile?.role || profile?.memberRole || profile?.orgRole,
  );
  const profileType = normalizeRole(profile?.driverType);

  return (
    claimRole === DRIVER_ROLE ||
    profileRole === DRIVER_ROLE ||
    (!profileRole && profileType === DRIVER_ROLE)
  );
}

function authErrorMessage() {
  return "密碼錯誤";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setError(authErrorMessage());
      return;
    }

    setError("");
    setLoading(true);

    let authenticatedUser = null;
    try {
      const result = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password,
      );
      const user = result.user;
      authenticatedUser = user;
      const tokenResult = await getIdTokenResult(user, true);
      const claims = tokenResult.claims || {};
      const portalType = resolvePortalFromClaims(user, claims);

      const rejectDriverFromWeb = async () => {
        await signOut(auth).catch(() => {});
        window.location.replace(DRIVER_BLOCK_REDIRECT_URL);
      };

      if (isDriverAccount(claims, null) || portalType === "driver") {
        await rejectDriverFromWeb();
        return;
      }

      const profileSnapshot = await getDoc(doc(db, "drivers", user.uid));
      const profile = profileSnapshot.exists() ? profileSnapshot.data() : null;
      if (isDriverAccount(claims, profile)) {
        await rejectDriverFromWeb();
        return;
      }

      if (!portalType) {
        await signOut(auth);
        setError(authErrorMessage());
        return;
      }

      const requestedPath = location.state?.from;
      const safeRequestedPath =
        typeof requestedPath === "string" && requestedPath.startsWith("/")
          ? requestedPath
          : "";

      if (portalType === "systemAdmin") {
        navigate("/systemadmin", { replace: true });
      } else if (
        portalType === "internal" &&
        !safeRequestedPath.startsWith("/systemadmin") &&
        isAllowedInternalPath(safeRequestedPath)
      ) {
        navigate(safeRequestedPath, { replace: true });
      } else {
        navigate(INTERNAL_ROUTES.home, { replace: true });
      }
    } catch (loginError) {
      // 對外不透露帳號是否存在、停用狀態、Firebase 或權限細節。
      if (authenticatedUser) {
        await signOut(auth).catch(() => {});
      }
      void loginError;
      setError(authErrorMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={styles.page}>
      <style>{"@keyframes tallarge-login-spin{to{transform:rotate(360deg)}}"}</style>
      <section style={styles.shell}>
        <img
          src={TallargeLogoFull}
          alt="Tallarge"
          style={styles.logo}
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />

        <h1 style={styles.title}>登入平台</h1>

        <form onSubmit={handleLogin} style={styles.form} noValidate>
          <label style={styles.label} htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="text"
            inputMode="email"
            placeholder="輸入 Email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (error) setError("");
            }}
            style={styles.input}
            autoComplete="username"
            spellCheck="false"
            autoCapitalize="none"
          />

          <label style={styles.passwordLabel} htmlFor="login-password">密碼</label>
          <input
            id="login-password"
            type="password"
            placeholder="輸入密碼"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              if (error) setError("");
            }}
            style={styles.input}
            autoComplete="current-password"
          />

          {error ? <div style={styles.error} role="alert">{error}</div> : null}

          <button type="submit" style={styles.button} disabled={loading}>
            <span>{loading ? "登入中" : "登入"}</span>
            {!loading && <span style={styles.buttonArrow}>→</span>}
            {loading && <span style={styles.spinner} aria-hidden="true" />}
          </button>
        </form>

        <p style={styles.securityNote}>安全連線 · Tallarge Workspace</p>
      </section>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    padding: "24px 28px 30px",
    overflowX: "hidden",
    background: "#FFFFFF",
    color: "#17212B",
    fontFamily: 'Inter, "Noto Sans TC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  shell: {
    width: "100%",
    maxWidth: 420,
    margin: "0 auto",
  },
  logo: {
    display: "block",
    width: "min(220px, 58vw)",
    maxWidth: "100%",
    height: "auto",
    objectFit: "contain",
    objectPosition: "left center",
    marginBottom: 46,
  },
  title: {
    margin: 0,
    color: "#101B26",
    fontSize: "clamp(30px, 4vw, 34px)",
    lineHeight: 1.15,
    letterSpacing: "-0.06em",
    fontWeight: 850,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    marginTop: 34,
  },
  label: {
    marginBottom: 7,
    color: "#4C5963",
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 800,
  },
  passwordLabel: {
    marginTop: 16,
    marginBottom: 7,
    color: "#4C5963",
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 800,
  },
  input: {
    width: "100%",
    height: 50,
    boxSizing: "border-box",
    border: "1px solid #D4DFEB",
    borderRadius: 14,
    padding: "0 14px",
    outline: "none",
    background: "#EAF2FF",
    color: "#182531",
    fontSize: 14,
    transition: "border-color .18s, box-shadow .18s, background .18s",
  },
  error: {
    marginTop: 14,
    padding: "12px 14px",
    border: "1px solid #F0D2CE",
    borderRadius: 10,
    background: "#FFF6F4",
    color: "#A64F46",
    fontSize: 13,
    fontWeight: 750,
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 13,
    width: "100%",
    height: 51,
    marginTop: 24,
    border: 0,
    borderRadius: 14,
    background: "#17212B",
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 850,
    cursor: "pointer",
    boxShadow: "0 12px 22px rgba(23,33,43,.13)",
    transition: "transform .18s, opacity .18s",
  },
  buttonArrow: {
    fontSize: 21,
    lineHeight: 1,
    fontWeight: 400,
  },
  spinner: {
    width: 16,
    height: 16,
    border: "2px solid rgba(255,255,255,.35)",
    borderTopColor: "#FFFFFF",
    borderRadius: "50%",
    animation: "tallarge-login-spin .75s linear infinite",
  },
  securityNote: {
    margin: "25px 0 0",
    color: "#A3ADB4",
    fontSize: 11,
    textAlign: "center",
  },
};
